import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ShoppingBag, Menu, X, PackageSearch, CheckCircle2, User, LogOut,
  HomeIcon, Info, Heart, LayoutGrid, FileText, Phone, Film, UserPlus, Box, Briefcase, ChevronDown, ChevronRight, Smartphone, Download, ReceiptText,
} from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import { orderCategories } from "@/lib/pricing";
import AltosLoginDialog from "@/components/AltosLoginDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFab";

export default function Header() {
  const { count, verified, altosName, logoutAltos } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const navigate = useNavigate();
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.categories() });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const orderedCats = orderCategories(categories);
  const appUrl = settings?.android_app_url || "";

  const close = () => setMenuOpen(false);
  const bulkHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Altos World, I'd like to place a bulk order.")}`;
  const franchiseHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Altos World, I'm interested in becoming a Franchise Centre.")}`;

  const Row = ({ to, href, icon: Icon, label, onClick, testid }) => {
    const content = (
      <div className="flex items-center gap-4 py-4 border-b border-line">
        <Icon className="text-brand shrink-0" size={22} />
        <span className="flex-1 text-lg font-medium text-ink">{label}</span>
      </div>
    );
    if (href) return <a href={href} target="_blank" rel="noopener noreferrer" onClick={close} data-testid={testid} className="block">{content}</a>;
    return <Link to={to} onClick={onClick || close} data-testid={testid} className="block">{content}</Link>;
  };

  return (
    <header className="sticky top-0 z-40 bg-paper border-b border-line shadow-sm">
      <div className="h-1 w-full bg-gradient-to-r from-brand via-brandsec to-crimson" />
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex items-center justify-between h-[88px] gap-4">
          <button className="p-2 -ml-2 text-ink" onClick={() => setMenuOpen(true)} data-testid="menu-button" aria-label="menu">
            <Menu size={26} />
          </button>

          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="logo-link">
            <img src="/altos-logo.jpg" alt="Altos World" className="h-16 sm:h-20 w-auto object-contain" />
            <span className="block max-w-[86px] sm:max-w-[120px] md:max-w-[140px] text-[7px] sm:text-[8px] md:text-[9px] leading-tight font-semibold uppercase tracking-wide text-muted2 border-l border-line pl-1.5 sm:pl-2.5" data-testid="authorised-distributor">
              An Authorised Distributor of Altos Enterprises Limited
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 ml-4 flex-1">
            <Link to="/" data-testid="nav-home" className="text-sm font-medium text-ink hover:text-brand transition-colors">Shop</Link>
            <Link to="/concerns" data-testid="nav-concerns" className="text-sm font-medium text-ink hover:text-brand transition-colors">Shop by Concern</Link>
            <Link to="/blogs" data-testid="nav-blogs" className="text-sm font-medium text-ink hover:text-brand transition-colors">Blogs</Link>
            <Link to="/orders" data-testid="nav-orders" className="text-sm font-medium text-ink hover:text-brand transition-colors flex items-center gap-1.5"><PackageSearch size={16} /> Track Orders</Link>
            {verified && <Link to="/billing" data-testid="nav-billing" className="text-sm font-semibold text-crimson hover:opacity-80 transition-colors flex items-center gap-1.5"><ReceiptText size={16} /> Retail Billing</Link>}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block"><LanguageSwitcher /></div>
            {appUrl && (
              <a href={appUrl} target="_blank" rel="noopener noreferrer" aria-label="Download our Android App" data-testid="header-download-app" className="flex items-center gap-1.5 text-xs font-semibold text-white bg-charcoal rounded-full px-3 py-1.5 hover:bg-charcoal/90 transition-colors">
                <Smartphone size={14} className="text-brandsec" /> <span className="hidden sm:inline">Get the App</span>
              </a>
            )}
            {verified ? (
              <button onClick={logoutAltos} data-testid="altos-logout-button" className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-ok border border-ok/40 bg-brandter rounded-full px-3 py-1.5 max-w-[180px]">
                <CheckCircle2 size={14} className="shrink-0" /> <span className="truncate">Hi, {altosName ? altosName.split(" ")[0] : "Member"}</span> <LogOut size={12} className="shrink-0" />
              </button>
            ) : (
              <button onClick={() => setLoginOpen(true)} data-testid="altos-login-button" className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-brand border border-brand/30 rounded-full px-3 py-1.5 hover:bg-mist transition-colors">
                <User size={14} /> Altos ID Login
              </button>
            )}
            <button onClick={() => navigate("/cart")} data-testid="cart-button" className="relative p-2 text-ink hover:text-brand transition-colors" aria-label="cart">
              <ShoppingBag size={22} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-brand text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="absolute left-0 top-0 h-full w-[86%] max-w-sm bg-paper flex flex-col fade-up">
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h2 className="font-display text-4xl text-ink">Menu</h2>
              <button className="p-1 text-ink" onClick={close} data-testid="menu-close"><X size={26} /></button>
            </div>

            <div className="px-6 pb-2"><LanguageSwitcher testid="language-select-mobile" /></div>

            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {/* Download Android app (top) */}
              {appUrl && (
                <a href={appUrl} target="_blank" rel="noopener noreferrer" onClick={close} data-testid="menu-download-app-top" className="flex items-center gap-3 rounded-xl bg-charcoal text-white px-4 py-3 mt-1 mb-3 hover:bg-charcoal/90 transition-colors">
                  <Smartphone size={22} className="text-brandsec shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">Download our Android App</p>
                    <p className="text-[11px] text-white/60">Shop faster & get order updates</p>
                  </div>
                  <Download size={18} />
                </a>
              )}

              {/* Altos login / status */}
              {verified ? (
                <button onClick={() => { close(); logoutAltos(); }} className="w-full flex items-center gap-4 py-4 border-b border-line text-left" data-testid="menu-altos-logout">
                  <CheckCircle2 className="text-ok shrink-0" size={22} />
                  <span className="flex-1 text-lg font-medium text-ok">Hi, {altosName || "Member"} — Log out</span>
                </button>
              ) : (
                <button onClick={() => { close(); setLoginOpen(true); }} className="w-full flex items-center gap-4 py-4 border-b border-line text-left" data-testid="menu-altos-login">
                  <User className="text-brand shrink-0" size={22} />
                  <span className="flex-1 text-lg font-medium text-ink">Altos ID Login</span>
                </button>
              )}

              <Row to="/" icon={HomeIcon} label="Home" testid="menu-home" />
              {verified && <Row to="/billing" icon={ReceiptText} label="Retail Billing" testid="menu-billing" />}
              <Row to="/about" icon={Info} label="About Us" testid="menu-about" />
              <Row to="/concerns" icon={Heart} label="Shop by Concern" testid="menu-concerns" />

              {/* Categories expandable */}
              <button onClick={() => setCatOpen((o) => !o)} className="w-full flex items-center gap-4 py-4 border-b border-line text-left" data-testid="menu-categories">
                <LayoutGrid className="text-brand shrink-0" size={22} />
                <span className="flex-1 text-lg font-medium text-ink">Categories</span>
                {catOpen ? <ChevronDown className="text-muted2" size={20} /> : <ChevronRight className="text-muted2" size={20} />}
              </button>
              {catOpen && (
                <div className="border-b border-line">
                  {orderedCats.map((c) => (
                    <button key={c} onClick={() => { close(); navigate(`/?category=${encodeURIComponent(c)}`); }} data-testid={`menu-cat-${c}`} className="w-full text-left pl-12 py-3 text-base text-ink/80 hover:text-brand">
                      {c}
                    </button>
                  ))}
                </div>
              )}

              <Row to="/terms" icon={FileText} label="Terms and Conditions" testid="menu-terms" />
              <Row to="/contact" icon={Phone} label="Contact Us" testid="menu-contact" />
              <Row to="/blogs" icon={Film} label="Blogs & Vlogs" testid="menu-blogs" />
              <Row to="/orders" icon={PackageSearch} label="Track My Orders" testid="menu-orders" />
              <Row to="/register" icon={UserPlus} label="Register as Direct Seller" testid="menu-register" />
              <Row href={bulkHref} icon={Box} label="For Bulk Orders — Click Here" testid="menu-bulk" />
              <Row href={franchiseHref} icon={Briefcase} label="Want to become a Franchise Centre? Click Here" testid="menu-franchise" />
              {appUrl && <Row href={appUrl} icon={Smartphone} label="Download our Android App" testid="menu-download-app-bottom" />}

              <p className="text-sm text-muted2 mt-6">Altos World · Cuttack Super Zone</p>
            </div>
          </div>
        </div>
      )}

      <AltosLoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </header>
  );
}
